import clsx from 'clsx'
import { Ref, ReactNode, useEffect, useRef, useState } from 'react'
import { FieldPath, FieldValues, useController } from 'react-hook-form'
import { ControlledInputProps, FieldProps } from '../../types'
import { snakeToTitle } from '../../utils'
import './TextInput.css'
import { CloseIcon, EyeIcon, EyeOffIcon, UploadIcon } from '../icons'
import Button from '../Button'

type TextInputValue = string | number | readonly string[] | undefined
export interface BaseTextInputProps<T extends TextInputValue> extends Partial<ControlledInputProps<T>> {
    type?: 'text' | 'time' | 'date' | 'datetime-local' | 'number' | 'password' | 'email'
    textarea?: boolean
    autosize?: boolean
    size?: 'tiny' | 'small' | 'regular'
    value?: T
    name: string
    placeholder?: string
    onChange?: (value: T) => void
    onBlur?: React.FocusEventHandler<HTMLTextAreaElement | HTMLInputElement>
    onFocus?: React.FocusEventHandler<HTMLTextAreaElement | HTMLInputElement>
    onPaste?: React.ClipboardEventHandler<HTMLTextAreaElement | HTMLInputElement>
    labelRef?: Ref<HTMLLabelElement>
    inputRef?: Ref<HTMLInputElement | HTMLTextAreaElement>
    hideLabel?: boolean
    min?: number | string
    minLength?: number
    max?: number | string
    maxLength?: number
    pattern?: RegExp
    patternMessage?: string
    minLengthMessage?: string
    fileUpload?: boolean
    fileUploadKey?: string
    icon?: ReactNode
    suffix?: ReactNode
    prefix?: ReactNode
    clearable?: boolean
}

export type TextInputProps<T extends TextInputValue> = BaseTextInputProps<T> & (
    | {
        textarea: true
        inputRef?: Ref<HTMLTextAreaElement>
    } | {
        textarea: false
        inputRef?: Ref<HTMLInputElement>
    } | {
        textarea?: undefined
        inputRef?: Ref<HTMLInputElement>
    }
)

export default function TextInput<X extends TextInputValue>({
    type = 'text',
    disabled,
    label,
    subtitle,
    min,
    minLength,
    max,
    maxLength,
    name,
    required,
    error,
    textarea,
    autosize = false,
    size = 'regular',
    value = '',
    onChange,
    onBlur,
    onFocus,
    onPaste,
    placeholder,
    labelRef,
    inputRef,
    hideLabel = false,
    icon,
    prefix,
    suffix,
    clearable,
    fileUpload,
    fileUploadKey,
}: TextInputProps<X>) {
    const internalTextareaRef = useRef<HTMLTextAreaElement | null>(null)
    const fileInputRef = useRef<HTMLInputElement>(null)
    const [fileName, setFileName] = useState<string>()
    const [fileError, setFileError] = useState<string>()
    const [revealPassword, setRevealPassword] = useState(false)

    // Credentials are often downloaded as a JSON wrapper (a Firebase service
    // account file, for example) rather than as the bare value the field wants.
    // `null` means the file was that wrapper but did not hold the value.
    const readJsonValue = (contents: string) => {
        if (!fileUploadKey) return undefined
        let parsed: any
        try {
            parsed = JSON.parse(contents)
        } catch {
            return undefined
        }
        if (parsed === null || typeof parsed !== 'object') return undefined
        const value = parsed[fileUploadKey]
        return typeof value === 'string' ? value.trim() : null
    }

    // Any new value makes a previous upload complaint stale.
    const emitChange = (next: X) => {
        setFileError(undefined)
        onChange?.(next)
    }

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]
        event.target.value = ''
        if (!file) return
        const reader = new FileReader()
        reader.onload = () => {
            const contents = String(reader.result ?? '')
            const jsonValue = readJsonValue(contents)
            if (jsonValue === null) {
                setFileName(undefined)
                setFileError(`${file.name} has no "${fileUploadKey}" in it. Pick the file you downloaded from your push provider.`)
                return
            }
            setFileName(file.name)
            emitChange((jsonValue ?? contents.trim()) as X)
        }
        reader.readAsText(file)
    }
    const isPassword = type === 'password'
    const inputType = isPassword && revealPassword ? 'text' : type
    const isMultiline = Boolean(textarea) && (!isPassword || revealPassword)

    // A single-line input drops the line breaks out of a pasted key, which
    // silently turns a valid credential into one the provider cannot parse.
    // While masked the element's value has those breaks stripped, so its
    // selection offsets do not line up with the value held here: a multi-line
    // paste replaces the field outright rather than splicing at the cursor.
    const handlePaste: React.ClipboardEventHandler<HTMLTextAreaElement | HTMLInputElement> = (event) => {
        if (Boolean(textarea) && !isMultiline) {
            const pasted = event.clipboardData.getData('text')
            if (pasted.includes('\n')) {
                event.preventDefault()
                emitChange(pasted.trim() as X)
            }
        }
        onPaste?.(event)
    }
    const passwordToggle = isPassword
        ? (
            <Button
                type="button"
                variant="plain"
                size="tiny"
                className={clsx('ui-text-input-reveal-button', isMultiline && 'ui-text-input-reveal-button-multiline')}
                aria-label={revealPassword ? 'Hide value' : 'Show value'}
                onClick={() => setRevealPassword(prev => !prev)}
                icon={revealPassword ? <EyeOffIcon /> : <EyeIcon />}
            />
        )
        : null

    const adjustHeight = (element: HTMLTextAreaElement | null) => {
        if (element && isMultiline && autosize) {
            element.style.height = 'auto'
            element.style.height = `${element.scrollHeight}px`
        }
    }

    useEffect(() => {
        adjustHeight(internalTextareaRef.current)
    }, [value, isMultiline, autosize])

    const assignInputRef = (element: HTMLTextAreaElement | HTMLInputElement | null) => {
        if (!inputRef) return
        if (typeof inputRef === 'function') {
            (inputRef as (instance: typeof element) => void)(element)
        } else {
            (inputRef as React.MutableRefObject<typeof element>).current = element
        }
    }

    const combinedRef = (element: HTMLTextAreaElement | null) => {
        internalTextareaRef.current = element
        assignInputRef(element)
        adjustHeight(element)
    }
    return (
        <label ref={labelRef} className={clsx('ui-text-input', { 'hide-label': hideLabel, disabled })}>
            {
                !hideLabel && (
                    <span>
                        {label ?? snakeToTitle(name)}
                        {required && <span style={{ color: 'red' }}>&nbsp;*</span>}
                    </span>
                )
            }
            {subtitle && <span className="label-subtitle">{subtitle}</span>}
            <div className={clsx(
                icon && 'ui-text-input-icon-wrapper',
                suffix && 'ui-text-input-suffix-wrapper',
                prefix && 'ui-text-input-prefix-wrapper',
                clearable && 'ui-text-input-clear-button-wrapper',
                isPassword && 'ui-text-input-reveal-button-wrapper',
                isPassword && isMultiline && 'ui-text-input-reveal-multiline',
            )}>
                {
                    prefix && (
                        <div className={clsx('ui-text-input-prefix', size)}>
                            {prefix}
                        </div>
                    )
                }
                {
                    isMultiline
                        ? (
                            <textarea
                                value={value}
                                onChange={(event) => emitChange(event?.target.value as X)}
                                onBlur={onBlur}
                                onFocus={onFocus}
                                onPaste={handlePaste}
                                ref={combinedRef}
                                minLength={minLength}
                                maxLength={maxLength}
                                disabled={disabled}
                                style={autosize ? { overflow: 'hidden', resize: 'none' } : undefined}
                            />
                        )
                        : (
                            <input
                                type={inputType}
                                value={value}
                                className={size}
                                placeholder={placeholder}
                                onChange={(event) => {
                                    const inputValue = typeof value === 'number' || type === 'number'
                                        ? event?.target.valueAsNumber
                                        : event?.target.value
                                    emitChange(inputValue as X)
                                }}
                                onBlur={onBlur}
                                onFocus={onFocus}
                                onPaste={handlePaste}
                                ref={assignInputRef}
                                min={min}
                                minLength={minLength}
                                max={max}
                                maxLength={maxLength}
                                disabled={disabled}
                            />
                        )
                }
                {
                    icon && (
                        <span className="ui-text-input-icon">
                            {icon}
                        </span>
                    )
                }
                {
                    clearable && value && (
                        <Button
                            className={clsx('ui-text-input-clear-button')}
                            variant="plain"
                            onClick={() => emitChange('' as X)}
                            aria-label="Clear"
                            size="tiny"
                            icon={<CloseIcon />}
                        />
                    )
                }
                {passwordToggle}
                {
                    suffix && (
                        <div className={clsx('ui-text-input-suffix', size)}>
                            {suffix}
                        </div>
                    )
                }
            </div>
            {(error ?? fileError) && !hideLabel && (
                <span className="field-error">
                    {error ?? fileError}
                </span>
            )}
            {fileUpload && (
                <div className="ui-text-input-file">
                    <input
                        ref={fileInputRef}
                        type="file"
                        className="ui-text-input-file-input"
                        disabled={disabled}
                        onChange={handleFileChange}
                    />
                    <Button
                        type="button"
                        variant="secondary"
                        size="small"
                        icon={<UploadIcon />}
                        disabled={disabled}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        {fileName ? `Replace file (${fileName})` : 'Upload file'}
                    </Button>
                </div>
            )}
        </label>
    )
}

TextInput.Field = function TextInputField<X extends FieldValues, P extends FieldPath<X>>({
    form,
    name,
    required,
    value,
    onChange,
    onBlur,
    pattern,
    patternMessage,
    minLengthMessage,
    ...rest
}: TextInputProps<P> & FieldProps<X, P>) {

    const { minLength, maxLength } = rest
    const { field: { ref, ...field }, fieldState } = useController({
        control: form.control,
        name,
        rules: {
            ...(required ? { required: 'This field is required.' } : {}),
            ...(minLength != null ? { minLength: { value: minLength, message: minLengthMessage ?? `Must be at least ${minLength} characters.` } } : {}),
            ...(maxLength != null ? { maxLength: { value: maxLength, message: `Must be at most ${maxLength} characters.` } } : {}),
            ...(pattern ? { pattern: { value: pattern, message: patternMessage ?? 'Invalid format.' } } : {}),
        },
    })

    return (
        <TextInput
            {...rest}
            {...field}
            inputRef={ref}
            onBlur={async (event) => {
                await field.onBlur?.()
                onBlur?.(event)
            }}
            onChange={async (event) => {
                await field.onChange?.(event)
                onChange?.(event)
            }}
            required={required}
            error={fieldState.error?.message}
        />
    )
}
